import { computed, type Signal, effect, inject } from "@angular/core";

import { FilterPersistence } from "./persistence/filter.persistence";
import { FilterRegistry } from "./registry/filter.registry";
import { SimpleFilterMulti } from "../filters/types/simple-filter-multi";
import type { SupportedFilterValue, FilterValue, FilterSelections, FilterField, FilterName, Predicate } from "@filter/filter.types";

export class FilterStore {
    private readonly persistence = inject(FilterPersistence);

    private readonly registry = inject(FilterRegistry);

    private persistGuard = false;
    private initializing = false;
    private firstRun = true;

    public readonly activeFilters = computed(() => this.registry.list().filter((field) => field.isActive()));
    public readonly hasActiveFilters = computed(() => this.activeFilters().length > 0);
    public readonly selections = computed(
        () => {
            const selections: FilterSelections = {};
            this.registry.list().forEach((field) => {
                const selection = field.selection();
                const fieldName = field.definition.filterName;
                selections[fieldName] = selection;
            });
            return selections;
        },
        {
            equal: (a, b) => {
                if (a === b) return true;
                const keys = Object.keys(a);
                if (keys.length !== Object.keys(b).length) return false;
                return keys.every((key) => a[key] === b[key]);
            },
        },
    );

    public loadSelections(persistenceId: string): void {
        this.persistGuard = true;
        this.persistence.setId(persistenceId);
        const loadedSelections = this.persistence.load();
        for (const filter of this.registry.list()) {
            filter.setSelection(loadedSelections?.[filter.definition.filterName]);
        }
        this.persistGuard = false;
    }

    public setFilters(filters: FilterField[]): void {
        this.persistGuard = true;
        const loadedSelections = this.persistence.load();
        this.registry.initialize(filters, loadedSelections);
        this.persistGuard = false;
        this.initializing = true;
    }

    public setSelection(fieldName: string, value: SupportedFilterValue): void {
        this.get(fieldName)?.setSelection(value);
    }

    public clear(fieldName: string): void {
        this.get(fieldName)?.clear();
    }

    public clearValue(fieldName: string, value: SupportedFilterValue): void {
        const filter = this.get(fieldName);
        if (!filter) return;
        if (filter instanceof SimpleFilterMulti) filter.removeValue?.(value as FilterValue);
        else filter.clear();
    }

    public clearAll(): void {
        this.persistGuard = true;
        this.registry.list().forEach((field) => field.clear());
        this.persist();
        this.persistGuard = false;
    }

    public applyFilter<TItem>(items: Signal<TItem[]>): Signal<TItem[]> {
        return computed(() => {
            const sourceItems = items();
            const predicates = this.activeFilters()
                .map((filter) => filter.predicate())
                .filter((predicate): predicate is Predicate<TItem> => predicate !== null);
            if (!predicates.length) return sourceItems;

            return sourceItems.filter((item) => predicates.every((predicate) => predicate(item)));
        });
    }

    private persist(): void {
        const selectionState = this.selections();
        this.persistence.save(selectionState);
    }

    public get(filterName: FilterName): FilterField | undefined {
        return this.registry.get(filterName);
    }

    constructor() {
        effect(() => {
            this.selections();
            if (this.persistGuard || !this.initializing) return;
            if (this.firstRun) {
                this.firstRun = false;
                return;
            }

            this.persist();
        });
    }
}
